import React, { ReactNode } from 'react';
import { Formik } from 'formik';
import * as Yup from 'yup';
import Bill from '../../../../../models/Bill';
import Input from '../../../../../components/form-control/Input';
import Button from '../../../../../components/form-control/Button';
import SelectDropdown from '../../../../../components/form-control/SelectDropdown';
import DatePickerInput from '../../../../../components/form-control/DatePickerInput';
import { PAYMENT_CHANNELS } from '../../../../../resources/enums';
import { getFormattedPaymentChannel, showToast } from '../../../../../utils/miscFunctions';
import { markBillAsPaidWithDetails } from '../../../../../api/requests';
import moment from 'moment';
import { CheckCircle, DollarSign, Calendar, CreditCard } from 'lucide-react';

interface IMarkAsPaidModalProps {
  bill: Bill;
  onProceed: () => void;
  onCancel: () => void;
}

export interface IBillsPaymentDetailsDTO {
  paymentAmount: number;
  paymentChannel: string;
  paymentDate: string;
  paymentReference: string;
}

const MarkAsPaidModal = ({ bill, onProceed, onCancel }: IMarkAsPaidModalProps) => {
  const [submitting, setSubmitting] = React.useState<boolean>(false);

  const paymentChannels = Object.keys(PAYMENT_CHANNELS).map((channel) => ({
    name: getFormattedPaymentChannel(channel),
    value: channel
  }));

  const handleSubmit = async (values: any) => {
    const payload: IBillsPaymentDetailsDTO = {
      ...values,
      paymentDate: moment(values.paymentDate).format('YYYY-MM-DD'),
    };

    setSubmitting(true);

    try {
      const response: any = await markBillAsPaidWithDetails(bill.id, payload);
      showToast.success(response?.message || 'Bill marked as paid successfully');
      onProceed();
    } catch (error: any) {
      showToast.error(error?.description || 'Failed to mark bill as paid');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-white dark:bg-darkBg dark:border dark:border-darkBg-border rounded-xl shadow-lg">
      <div className="p-6 border-b border-gray-200 dark:border-darkBg-border">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center">
            <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-darkBg-mainText">Mark Bill as Paid</h2>
            <p className="text-sm text-gray-600 dark:text-darkBg-subText">
              Bill #{bill.id} - GHS {bill.amount}
            </p>
          </div>
        </div>
      </div>

      <Formik
        initialValues={{
          paymentAmount: bill.amount || 0,
          paymentChannel: '',
          paymentDate: null,
          paymentReference: ''
        }}
        validateOnBlur
        validationSchema={Yup.object({
          paymentAmount: Yup.number().required('Payment amount is required.'),
          paymentChannel: Yup.string().required('Payment channel is required.'),
          paymentDate: Yup.string().required('Payment date is required.'),
          paymentReference: Yup.string().required('Payment reference is required.'),
        })}
        onSubmit={(values) => {
          handleSubmit(values);
        }}>
        {({ values, errors, handleSubmit, handleChange, handleBlur, touched, setFieldValue }) => (
          <form className="p-6" onSubmit={handleSubmit}>
            <div className="space-y-6">
              <div className="">
                <Input
                  type="number"
                  name="paymentAmount"
                  label="Payment Amount"
                  value={values.paymentAmount}
                  placeholder="0.00"
                  required
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched?.paymentAmount ? errors.paymentAmount : undefined}
                  helperText={touched?.paymentAmount ? errors.paymentAmount : undefined}
                />
              </div>

              <div className="">
                <SelectDropdown
                  name="paymentChannel"
                  label="Payment Channel"
                  listSelectedValue={values.paymentChannel}
                  list={paymentChannels}
                  onChange={(value: string) => setFieldValue('paymentChannel', value)}
                  onBlur={handleBlur}
                  error={touched?.paymentChannel ? errors.paymentChannel : undefined}
                  helperText={touched?.paymentChannel ? errors.paymentChannel : undefined}
                />
              </div>

              <DatePickerInput
                id={'paymentDate'}
                selected={values.paymentDate ? new Date(values.paymentDate) : null}
                label="Payment date"
                onChange={(date: Date | null) => {
                  setFieldValue('paymentDate', date);
                }}
                error={touched?.paymentDate ? errors.paymentDate : undefined}
                helperText={touched?.paymentDate ? errors.paymentDate : undefined}
              />

              <Input
                type="text"
                name="paymentReference"
                label="Payment Reference"
                value={values.paymentReference}
                placeholder="Enter transaction ID or reference"
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched?.paymentReference ? errors.paymentReference : undefined}
                helperText={touched?.paymentReference ? errors.paymentReference : undefined}
                required
              />
            </div>

            <div className="mt-8 flex gap-3">
              <Button
                label="Cancel"
                bgColor='secondary'
                fullWidth
                onClick={onCancel}
                className="py-3"
              />
              <Button
                label={submitting ? 'Processing...' : 'Mark as Paid'}
                onClick={handleSubmit}
                bgColor='primary'
                fullWidth
                loading={submitting}
                disabled={submitting}
                className="py-3"
              />
            </div>
          </form>
        )}
      </Formik>
    </div>
  );
};

export default MarkAsPaidModal;
